import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, qs } from "../api/client";
import { keys, useInvalidate, useLocations } from "../api/hooks";
import { createExclusionResponseSchema, exclusionsResponseSchema, removeExclusionResponseSchema, staffListResponseSchema, type StaffCandidate, type StaffExclusion } from "../../api/contracts";
import { formatInstant } from "../lib/format";
import { trackAction } from "../lib/usage-track";
import { Badge, Button, Dialog, EmptyState, ErrorNote, Field, Input, PageHeader, Panel, Select, Spinner, Toolbar } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";

/** "" = every location (admins only). */
type Scope = string;

/** Owners pick only from the people who take bookings; receptionists, managers and training logins stay out of their list. */
const OWNER_PICKABLE_ROLE = "aesthetician";
const isAesthetician = (role: string | null) => role?.trim().toLowerCase() === OWNER_PICKABLE_ROLE;

export function StaffPage({ variant }: { variant: "owner" | "admin" }) {
  const isAdmin = variant === "admin";
  const locations = useLocations();
  const invalidate = useInvalidate();
  const [scope, setScope] = useState<Scope | null>(null);
  useEffect(() => {
    if (scope !== null || !locations.data) return;
    setScope(isAdmin ? "" : locations.data[0]?.blvdId ?? null);
  }, [scope, isAdmin, locations.data]);
  const blvdId = scope || null;

  const exclusions = useQuery({ queryKey: keys.exclusions(blvdId), enabled: scope !== null, queryFn: () => api(`/api/exclusions${qs({ blvdId })}`, exclusionsResponseSchema) });
  const [search, setSearch] = useState("");
  useEffect(() => { setSearch(""); }, [scope]);
  // A location's roster is small enough to load whole and filter here; the business-wide list is searched on the server.
  const staffQuery = blvdId ? "" : search.trim();
  const staff = useQuery({
    queryKey: keys.staff(blvdId, staffQuery), enabled: scope !== null && (blvdId !== null || staffQuery.length >= 2),
    queryFn: () => api(`/api/exclusions/staff${qs({ blvdId, q: staffQuery })}`, staffListResponseSchema),
  });

  const [adding, setAdding] = useState<StaffCandidate | null>(null);
  const [reason, setReason] = useState("");
  const [removing, setRemoving] = useState<StaffExclusion | null>(null);
  const refresh = () => invalidate(["exclusions"]);
  const add = useMutation({
    mutationFn: (c: StaffCandidate) => api("/api/exclusions", createExclusionResponseSchema, { method: "POST", body: { staffId: c.staffId, blvdId, reason: reason.trim() || undefined } }),
    onSuccess: () => { setAdding(null); setReason(""); void refresh(); trackAction("exclusion_added", blvdId ? { blvdId } : undefined); },
  });
  const remove = useMutation({
    mutationFn: (x: StaffExclusion) => api(`/api/exclusions/${x.id}`, removeExclusionResponseSchema, { method: "DELETE" }),
    onSuccess: (_data, x) => { setRemoving(null); void refresh(); trackAction("exclusion_removed", x.blvdId ? { blvdId: x.blvdId } : undefined); },
  });

  if (locations.isPending) return <Spinner />;
  if (locations.isError) return <ErrorNote error={locations.error} />;
  if (!isAdmin && (locations.data ?? []).length === 0) return <EmptyState title="No locations yet" />;

  const rows = exclusions.data?.exclusions ?? [];
  const excludedHere = new Set(rows.filter((r) => r.blvdId === blvdId || r.blvdId === null).map((r) => r.staffId));
  const needle = search.trim().toLowerCase();
  const candidates = (staff.data?.staff ?? [])
    .filter((m) => isAdmin || isAesthetician(m.role))
    .filter((m) => !blvdId || !needle || m.name.toLowerCase().includes(needle));
  const where = (x: StaffExclusion) => x.blvdId === null ? "Every location" : x.locationName ?? "This location";
  const scopeLabel = blvdId ? (locations.data ?? []).find((l) => l.blvdId === blvdId)?.name ?? "this location" : "every location";

  const excludedColumns: Column<StaffExclusion>[] = [
    { key: "who", header: "Staff", cell: (x) => (
      <div><span className="font-medium">{x.staffName}</span>
        <p className="text-xs text-hs-text-muted">{x.reason ? `${x.reason} · ` : ""}{x.createdBy} {formatInstant(x.createdAt)}</p></div>
    ) },
    { key: "where", header: "Scope", width: "9rem", cell: (x) => <Badge tone={x.blvdId === null ? "primary" : "neutral"}>{where(x)}</Badge> },
    { key: "action", header: "", headerLabel: "Actions", align: "right", width: "8rem", cell: (x) => x.editable
      ? <Button variant="ghost" size="sm" aria-label={`Stop excluding ${x.staffName}`} onClick={() => setRemoving(x)}>Remove</Button>
      : <span className="text-xs text-hs-text-muted">Set by corporate</span> },
  ];
  const candidateColumns: Column<StaffCandidate>[] = [
    { key: "who", header: "Staff", cell: (m) => <><span className="font-medium">{m.name}</span>{m.role && <span className="text-hs-text-muted"> · {m.role}</span>}</> },
    { key: "state", header: "", headerLabel: "Exclusion", align: "right", width: "10rem", cell: (m) => m.autoExcluded ? <Badge tone="info">Skipped automatically</Badge>
      : excludedHere.has(m.staffId) ? <Badge>Excluded</Badge>
      : <Button variant="secondary" size="sm" aria-label={`Exclude ${m.name}`} onClick={() => { setReason(""); setAdding(m); }}>Exclude</Button> },
  ];

  return (
    <>
      <PageHeader title="Staff" subtitle={'Staff the planner leaves alone: shared training logins, room calendars and the like. They get no BTB blocks, and any already placed on their calendar are removed. Staff with the "Training Account" role are left alone automatically. This goes by the role in Boulevard, not the name. If a training login is set up as an aesthetician, add it here.'} />
      <Toolbar>
        <Field label="Location" className="w-72">
          <Select value={scope ?? ""} onChange={(e) => setScope(e.target.value)}>
            {isAdmin && <option value="">Every location</option>}
            {(locations.data ?? []).map((l) => <option key={l.blvdId} value={l.blvdId}>{l.name}</option>)}
          </Select>
        </Field>
      </Toolbar>
      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="Excluded" padding="none"
          footer={exclusions.data && exclusions.data.excludedRoles.length > 0 ? <p className="text-xs text-hs-text-muted">Also skipped automatically: anyone whose Boulevard role is {exclusions.data.excludedRoles.map((r) => `“${r}”`).join(" or ")}.</p> : undefined}>
          {exclusions.isPending && scope !== null && <div className="p-4"><Spinner /></div>}
          <div className="px-4 pt-4 empty:hidden"><ErrorNote error={exclusions.error} /></div>
          {exclusions.data && (rows.length === 0
            ? <p className="p-4 text-[13px] text-hs-text-muted">Nobody is excluded {blvdId ? "here" : "yet"}.</p>
            : <DataTable rows={rows} rowKey={(x) => x.id} density="compact" caption="Excluded staff" columns={excludedColumns} />)}
        </Panel>
        <Panel title={`Exclude someone ${blvdId ? "here" : "at every location"}`} padding="none">
          <div className="p-4">
            <Field label={blvdId ? "Filter" : "Search staff by name"} hint={blvdId ? undefined : "Type at least two letters."}>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name" />
            </Field>
            {staff.isFetching && <div className="mt-3"><Spinner /></div>}
            <ErrorNote error={staff.error} />
          </div>
          {staff.data && (candidates.length === 0
            ? <p className="px-4 pb-4 text-[13px] text-hs-text-muted">No matching staff.</p>
            : <DataTable rows={candidates} rowKey={(m) => m.staffId} density="compact" caption="Staff who can be excluded" columns={candidateColumns} />)}
        </Panel>
      </div>

      <Dialog open={adding !== null} title={`Exclude ${adding?.name} at ${scopeLabel}?`} onClose={() => setAdding(null)}
        footer={<><Button variant="secondary" disabled={add.isPending} onClick={() => setAdding(null)}>Cancel</Button><Button disabled={add.isPending} onClick={() => adding && add.mutate(adding)}>Exclude</Button></>}>
        <p>The planner stops adding BTB blocks for them{blvdId ? " here" : " anywhere"}, and removes the ones it already placed.</p>
        <Field label="Reason (optional)"><Input value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} placeholder="e.g. shared training login" /></Field>
        <ErrorNote error={add.error} />
      </Dialog>
      <Dialog open={removing !== null} title={`Stop excluding ${removing?.staffName}?`} onClose={() => setRemoving(null)}
        footer={<><Button variant="secondary" disabled={remove.isPending} onClick={() => setRemoving(null)}>Cancel</Button><Button variant="danger" disabled={remove.isPending} onClick={() => removing && remove.mutate(removing)}>Stop excluding</Button></>}>
        <p>Their upcoming days are planned again {removing?.blvdId === null ? "at every location they work" : "at this location"}, so BTB blocks may be added.</p>
        <ErrorNote error={remove.error} />
      </Dialog>
    </>
  );
}
